import { COLLAPSED_CLASS, setGraphClass } from '../lib/classes';
import { computeDominantLinkType } from '../lib/edge-summary';
import type { GraphCoordinates, GraphFrameSize } from '../contract';
import type {
  GraphEdgeDefinition,
  GraphElements,
  GraphNodeDefinition,
} from './types';
import type { GraphViewSnapshot } from './view-snapshot';

export type GroupFrame = GraphCoordinates & GraphFrameSize & { id: string };
export type CompoundState = {
  nodes: Map<string, GraphNodeDefinition>;
  edges: Map<string, GraphEdgeDefinition>;
  groups: Map<string, GraphViewSnapshot['viewState']['groups'][string]>;
  bundles: Map<string, GraphViewSnapshot['viewState']['edgeBundles'][string]>;
};
const clone = <T>(value: T): T => structuredClone(value);
const pair = (source: string, target: string) =>
  JSON.stringify([source, target].sort());
export const getCompoundOwners = (state: CompoundState) => {
  const owners = new Map<string, string>();
  for (const id of state.nodes.keys()) {
    let owner = id,
      parent = state.nodes.get(id)!.data.parent;
    while (typeof parent === 'string') {
      if (state.groups.get(parent)?.collapsed) owner = parent;
      parent = state.nodes.get(parent)?.data.parent;
    }
    owners.set(id, owner);
  }
  return owners;
};
export const getCompoundFrames = (
  state: CompoundState,
  descendants: (id: string) => string[],
): GroupFrame[] => {
  const visible = new Set(
    [...getCompoundOwners(state)]
      .filter(([id, owner]) => id === owner)
      .map(([id]) => id),
  );
  const measured = new Map<string, GroupFrame>();
  const measure = (id: string): GroupFrame => {
    const cached = measured.get(id);
    if (cached) return cached;
    const group = state.groups.get(id)!;
    const center = state.nodes.get(id)!.position ?? { x: 0, y: 0 };
    if (group.frameSize) {
      const frame = { id, ...center, ...group.frameSize };
      measured.set(id, frame);
      return frame;
    }
    const points = descendants(id)
      .filter((child) => visible.has(child))
      .flatMap((child) => {
        if (state.groups.has(child) && !state.groups.get(child)!.collapsed) {
          const frame = measure(child);
          return [
            { x: frame.x - frame.width / 2, y: frame.y - frame.height / 2 },
            { x: frame.x + frame.width / 2, y: frame.y + frame.height / 2 },
          ];
        }
        return [state.nodes.get(child)!.position ?? center];
      });
    if (!points.length) points.push(center);
    const left = Math.min(...points.map((p) => p.x)) - 35,
      right = Math.max(...points.map((p) => p.x)) + 35;
    const bottom = Math.min(...points.map((p) => p.y)) - 35,
      top = Math.max(...points.map((p) => p.y)) + 35;
    const frame = {
      id,
      x: (left + right) / 2,
      y: (bottom + top) / 2,
      width: right - left,
      height: top - bottom,
    };
    measured.set(id, frame);
    return frame;
  };
  return [...state.groups]
    .filter(([id, group]) => !group.collapsed && visible.has(id))
    .map(([id]) => measure(id));
};

export const projectCompoundGraph = (
  state: CompoundState,
  edgeMembers: (id: string) => string[],
): GraphElements => {
  const owners = getCompoundOwners(state);
  const nodes = [...state.nodes.values()]
    .filter((node) => owners.get(node.data.id) === node.data.id)
    .map((node) => {
      const group = state.groups.get(node.data.id);
      return {
        ...clone(node),
        data: {
          ...clone(node.data),
          ...(group
            ? {
                viewGroup: true,
                viewGroupKind: group.kind,
                viewGroupExpanded: !group.collapsed,
              }
            : {}),
        },
        classes: setGraphClass(
          node.classes,
          COLLAPSED_CLASS,
          !!group?.collapsed,
        ),
      };
    });
  const suppressed = new Set(
    [...state.groups.values()].flatMap((group) => group.suppressedEdgeIds),
  );
  const ownedMembers = new Set(
    [...state.bundles.values()].flatMap((bundle) => bundle.memberIds),
  );
  const edges: GraphEdgeDefinition[] = [];
  const project = (ids: string[], bundleId?: string) => {
    const buckets = new Map<string, GraphEdgeDefinition[]>();
    for (const id of ids) {
      const edge = state.edges.get(id);
      if (!edge || suppressed.has(id)) continue;
      const source = owners.get(edge.data.source),
        target = owners.get(edge.data.target);
      if (
        !source ||
        !target ||
        (source === target && edge.data.source !== edge.data.target)
      )
        continue;
      const key = bundleId
        ? pair(source, target)
        : JSON.stringify([source, target]);
      if (!buckets.has(key)) buckets.set(key, []);
      buckets.get(key)!.push(edge);
    }
    for (const [key, members] of buckets) {
      const first = members[0];
      const source = owners.get(first.data.source)!,
        target = owners.get(first.data.target)!;
      const redirected = members.some(
        (edge) =>
          owners.get(edge.data.source) !== edge.data.source ||
          owners.get(edge.data.target) !== edge.data.target,
      );
      if (!bundleId && !redirected) {
        edges.push(...members.map(clone));
        continue;
      }
      const dominantLink =
        state.bundles.get(bundleId ?? '')?.dominantLink ??
        computeDominantLinkType(members).dominantType;
      edges.push({
        data: {
          ...clone(first.data),
          id:
            bundleId && buckets.size === 1
              ? bundleId
              : `meta-${bundleId ?? ''}-${key}`,
          source,
          target,
          type: bundleId ? 'compound' : 'meta',
          ...(bundleId ? { bundleId } : {}),
          memberIds: members.map((edge) => edge.data.id),
          link: dominantLink
            ? { name: dominantLink, label: String(members.length) }
            : undefined,
          size: Math.max(1.5, Math.sqrt(members.length)),
          label: String(members.length),
        },
        classes: ['compound-edge'],
      });
    }
  };
  project([...state.edges.keys()].filter((id) => !ownedMembers.has(id)));
  for (const id of state.bundles.keys())
    if (!ownedMembers.has(id)) project(edgeMembers(id), id);
  return { nodes, edges };
};